import { useEffect, useMemo, useState } from 'react'

import { rtkApi } from 'src/Services/rtkApi'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import {
  OrderItemType,
  OrderListItem,
  OrderStatus,
  useGetOrdersQuery,
} from 'src/Redux/artistOrderApi'

import { useAppDispatch } from './redux'

const POLL_INTERVAL_MS = 3000
const MAX_POLL_TICKS = 20
const RECENT_ORDER_WINDOW_MS = 900000

const PLATFORM_TO_ORDER_ITEM_TYPE: Partial<
  Record<CampaignPlatformTypeEnum, OrderItemType>
> = {
  [CampaignPlatformTypeEnum.SPOTIFY]: OrderItemType.SpotifyCampaign,
  [CampaignPlatformTypeEnum.TIKTOK]: OrderItemType.TiktokCampaign,
  [CampaignPlatformTypeEnum.META]: OrderItemType.AdCampaign,
}

interface UseOrderPaymentPollingParams {
  enabled?: boolean
  platform?: CampaignPlatformTypeEnum
  campaignId?: number
  awaitingStatuses?: OrderStatus[]
  maxPollTicks?: number
}

interface UseOrderPaymentPollingResult {
  isAwaitingPayment: boolean
  isPaid: boolean
  isFailed: boolean
  failedOrderId?: number
  isTimedOut: boolean
  isResolved: boolean
}

const DEFAULT_AWAITING_STATUSES = [OrderStatus.Checkout]

const isAwaitingPaymentOrder = (
  order: OrderListItem,
  awaitingStatuses: OrderStatus[],
): boolean =>
  awaitingStatuses.includes(order.status) &&
  Date.now() - new Date(order.updatedAt).getTime() < RECENT_ORDER_WINDOW_MS

const containsCampaign = (
  order: OrderListItem,
  itemType: OrderItemType,
  campaignId: number,
): boolean =>
  order.items.some(
    (item) => item.type === itemType && item.sourceId === campaignId,
  )

export const useOrderPaymentPolling = ({
  enabled = true,
  platform,
  campaignId,
  awaitingStatuses = DEFAULT_AWAITING_STATUSES,
  maxPollTicks = MAX_POLL_TICKS,
}: UseOrderPaymentPollingParams = {}): UseOrderPaymentPollingResult => {
  const dispatch = useAppDispatch()
  const [hasTimedOut, setHasTimedOut] = useState(false)

  const { data: orders, isError: isOrdersError } = useGetOrdersQuery(void 0, {
    skip: !enabled,
  })

  const campaignOrders = useMemo(() => {
    if (!enabled || !orders) {
      return []
    }

    const itemType = platform ? PLATFORM_TO_ORDER_ITEM_TYPE[platform] : void 0

    return itemType && campaignId
      ? orders.filter((order) => containsCampaign(order, itemType, campaignId))
      : orders
  }, [campaignId, enabled, orders, platform])

  const isAwaitingPayment = useMemo(
    () =>
      campaignOrders.some((order) =>
        isAwaitingPaymentOrder(order, awaitingStatuses),
      ),
    [campaignOrders, awaitingStatuses],
  )

  const isPaid = useMemo(
    () => campaignOrders.some((order) => order.status === OrderStatus.Paid),
    [campaignOrders],
  )

  const failedOrderId = useMemo(
    () =>
      campaignOrders.find(
        (order) => order.status === OrderStatus.CheckoutFailed,
      )?.id,
    [campaignOrders],
  )

  useEffect(() => {
    if (!isAwaitingPayment) {
      return void 0
    }

    let ticksLeft = maxPollTicks

    const intervalId = setInterval(() => {
      ticksLeft -= 1

      if (ticksLeft <= 0) {
        clearInterval(intervalId)
        setHasTimedOut(true)
        return
      }

      dispatch(rtkApi.util.invalidateTags(['ArtistOrder']))
    }, POLL_INTERVAL_MS)

    return () => clearInterval(intervalId)
  }, [dispatch, isAwaitingPayment, maxPollTicks])

  const isTimedOut = isAwaitingPayment && hasTimedOut

  return {
    isPaid,
    failedOrderId,
    isTimedOut,
    isFailed: failedOrderId !== void 0,
    isAwaitingPayment: isAwaitingPayment && !hasTimedOut,
    isResolved: !enabled || isOrdersError || orders !== void 0,
  }
}
